import { useEffect, useMemo } from "react";

type Rotation = { rotation: number };

export type NewFile = { type: "new"; file: File } & Rotation;

export type ExistingFile = {
  type: "existing";
  file: {
    id: string;
    url: string;
    name: string;
    size: number;
  };
} & Rotation;

export type NewOrExistingFile = NewFile | ExistingFile;

function isNewFile(file: NewOrExistingFile): file is NewFile {
  return file.type === "new";
}

export function fileToNewFile(file: File): NewFile {
  return { type: "new", rotation: 0, file };
}

export function filesToNewFiles(files: File[]): NewFile[] {
  return files.map(fileToNewFile);
}

/**
 * Note that existing files in the given array will be filtered out.
 */
export function newOrExistingFilesToFiles(
  newOrExistingFiles: NewOrExistingFile[]
): File[] {
  return newOrExistingFiles.filter(isNewFile).map(({ file }) => file);
}

export function createNewOrExistingFileKey(
  newOrExistingFileKey: NewOrExistingFile
) {
  switch (newOrExistingFileKey.type) {
    case "new": {
      return newOrExistingFileKey.file.name + newOrExistingFileKey.file.size;
    }
    case "existing": {
      return newOrExistingFileKey.file.id;
    }
  }
}

/**
 * Get urls for the given new and existing files.
 * Takes care of revoking the object urls for the new files.
 */
export function useNewOrExistingFileUrls(
  newOrExistingFiles: NewOrExistingFile[]
) {
  const urls = useMemo(() => {
    return newOrExistingFiles.map((newOrExistingFile) => {
      switch (newOrExistingFile.type) {
        case "new": {
          return URL.createObjectURL(newOrExistingFile.file);
        }
        case "existing": {
          return newOrExistingFile.file.url;
        }
      }
    });
  }, [newOrExistingFiles]);

  useEffect(() => {
    return () => {
      urls.forEach(URL.revokeObjectURL);
    };
  }, [urls]);

  return urls;
}

/**
 * Get url for the given new and existing file.
 * Takes care of revoking the object url if it is a new file.
 */
export function useNewOrExistingFileUrl(newOrExistingFile: NewOrExistingFile) {
  const newOrExistingFiles = useMemo(() => {
    return [newOrExistingFile];
  }, [newOrExistingFile]);

  return useNewOrExistingFileUrls(newOrExistingFiles)[0]!;
}
